<script setup>
    import { ref, watchEffect } from 'vue';
    import { getFromAPI, buildPageFooter, isButtonDisabled } from '../functionCollection';
    import axios from 'axios';
    import Swal from 'sweetalert2';

    const props = defineProps(['fetchUrl', 'state', 'deleteUrl', 'antragUrl']);
    const waitingList = ref(null);
    const columnsToFetch = "id,antrag_id,rechnungsempfaenger,wanted_tarif,created_at";

    // for the page footer
    const currentPage = ref(1);
    const maxPageNumber = ref(1);
    const requestsOnPage = ref(null);
    const footerPageNumbers = ref(new Array());

    watchEffect(async () => {
        const url = getFullUrl(props.fetchUrl);
        waitingList.value = await getFromAPI(url);
        maxPageNumber.value = waitingList.value.meta.last_page;
        waitingList.value = waitingList.value.data;
        requestsOnPage.value = waitingList.value.length;
        footerPageNumbers.value = buildPageFooter(currentPage.value, maxPageNumber.value);
    });

    // add the URL parameters to the given URL, usually a prop.
    function getFullUrl(url) {
        return url = url.concat('/').concat(`?page=${currentPage.value}&per_page=25&columns=${columnsToFetch}`);
    }

    // replace the placeholder ':id' with the actual ID.
    function replaceIDinUrl(url, id) {
        let formattedUrl = url.replace(':id', id);
        return formattedUrl;
    }

    // replace the placeholder ':antrag_id' with the actual ID. could maybe be merged into the above function, but eh.
    function replaceAntragIDinUrl(url, id) {
        let formattedUrl = url.replace(':antrag_id', id);
        return formattedUrl;
    }

    // Opens a SweetAlert2 modal asking for confirmation if an entry should be deleted.
    // If yes, performs delete. When successful and confirmed, reloads page.
    async function deleteEntry(id) {
        Swal.fire({
            title: "Wartelisteneintrag löschen?",
            text: "Sind Sie sicher, dass Sie diesen Eintrag der Warteliste löschen wollen?",
            icon: 'warning',
            confirmButtonText: "Löschen",
            confirmButtonColor: "#e64942",
            showCancelButton: true,
            cancelButtonText: "Abbrechen",
            focusCancel: true,
            reverseButtons: true,
        }).then((result) => {
            if (result.isConfirmed) {
                axios.delete(replaceIDinUrl(props.deleteUrl, id))
                .then((data) => {
                    if (data.status === 200) {
                        Swal.fire({
                            title: 'Löschen erfolgreich',
                            text: 'Eintrag wurde erfolgreich gelöscht.',
                            icon: 'success',
                        }).then((result) => {
                            if (result.isConfirmed === true) {
                                window.location.reload();
                            }
                        })
                    }
                })
            }
        }).catch((error) => {
            Swal.fire({
                title: 'Löschen fehlgeschlagen!',
                text: 'Leider ist das Löschen fehlgeschlagen. Mehr Infos: ' + error.response.data.message,
                icon: 'error',
            })
        })
    }

    // calls new waitingList whenever a page change is requested (i.e. a page button is pressed in the footer)
    async function getNewPage(newPage) {
        currentPage.value = newPage;
        if (currentPage.value < 1) {
            currentPage.value = 1;
        }
        waitingList.value = await getFromAPI(getFullUrl(props.fetchUrl));
        maxPageNumber.value = waitingList.value.meta.last_page;
        waitingList.value = waitingList.value.data;
        requestsOnPage.value = waitingList.value.length;
        buildPageFooter(currentPage.value, maxPageNumber.value);
    }

</script>
<template>
    <div class="main-table" v-if="waitingList && waitingList.length > 0">
        <div class="inner-table">
            <table class="table">
                <thead>
                    <tr>
                        <th class="table-head">ID</th>
                        <th class="table-head">Antragsnummer</th>
                        <th class="table-head">Rechnungsempfängernummer</th>
                        <th class="table-head">Bestellter Tarif</th>
                        <th class="table-head">Eingetragen am</th>
                        <th class="table-head" v-if="props.state !== 'deleted'">Aktionen</th>
                    </tr>
                </thead>
                <tbody>
                    <tr class="m-datatable__row" v-for="entry of waitingList" :key="entry.id">
                        <td>{{ entry.id }}</td>
                        <td>{{ entry.antrag_id }}</td>
                        <td>{{ entry.rechnungsempfaenger }}</td>
                        <td>{{ entry.wanted_tarif }}</td>
                        <td>{{ entry.created_at }}</td>
                        <td v-if="props.state !== 'deleted'">
                            <div class="buttons d-grid gap-2">
                                <button
                                    type="button"
                                    class="btn btn-primary"
                                    @click.prevent="deleteEntry(entry.id)">
                                    Entfernen
                                </button>
                                <a  class="btn btn-primary"
                                    :href="replaceAntragIDinUrl(props.antragUrl, entry.antrag_id)"
                                    role="button">
                                    Antrag
                                </a>
                            </div>
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>
    </div>

    <div class="table-footer middle" v-if="waitingList && waitingList.length > 0 && footerPageNumbers.length > 1">
        <ul class="pagination">
            <li class="page-item">
                <button class="page-link" :disabled="isButtonDisabled('start')" @click="getNewPage(currentPage - 1)">
                    <i class="fas fa-lg fa-caret-left"></i>
                </button>
            </li>
            <li v-for="page in footerPageNumbers" :key="page.id" :class="{ 'active' : page == currentPage }" >
                <button class="page-link" @click="getNewPage(page)" :disabled="isButtonDisabled(page)">{{ page }}</button>
            </li>
            <li class="page-item">
                <button class="page-link" @click="getNewPage(currentPage + 1)" :disabled="isButtonDisabled('end')">
                    <i class="fas fa-lg fa-caret-right"></i>
                </button>
            </li>
        </ul>
    </div>

    <div class="m-3" v-if="waitingList && (waitingList.length == 0 || waitingList == null)">
        <div class="alert alert-custom" role="alert">
            <i class="fa-solid fa-comment fa-4x"></i>
            <span>Es wurden keine wartenden Anträge gefunden.</span>
        </div>
    </div>

</template>
